import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, User, Flame, Trophy, BookOpen, MessageCircle, Award,
  Brain, Moon, Wind, Sparkles, Zap, Compass, Sun, MoonStar, Heart,
  Bookmark, GraduationCap,
} from 'lucide-react';
import { useContent } from '../context/ContentContext';
import { getCategoryStyle } from '../utils/categoryStyles';

const iconMap = {
  Flame, BookOpen, GraduationCap, Brain, Moon, Wind, Zap, Trophy,
  Heart, Bookmark, Compass, Sparkles, Sun, MoonStar,
};

const BADGE_CATALOG = [
  { id: 'first-flame', name: 'First Flame', description: 'Read your first reflection', icon: 'Flame' },
  { id: 'bookworm', name: 'Bookworm', description: 'Read 5 reflections', icon: 'BookOpen' },
  { id: 'scholar', name: 'Scholar', description: 'Read 15 reflections', icon: 'GraduationCap' },
  { id: 'zen-master', name: 'Zen Master', description: 'Read all meditations', icon: 'Brain' },
  { id: 'dream-weaver', name: 'Dream Weaver', description: 'Read all sleep stories', icon: 'Moon' },
  { id: 'breath-of-life', name: 'Breath of Life', description: 'Read all breathing exercises', icon: 'Wind' },
  { id: 'streak-3', name: 'Streak Starter', description: '3-day streak', icon: 'Zap' },
  { id: 'streak-7', name: 'On Fire', description: '7-day streak', icon: 'Flame' },
  { id: 'streak-30', name: 'Unstoppable', description: '30-day streak', icon: 'Trophy' },
  { id: 'compassionate', name: 'Compassionate', description: 'Like 5 posts', icon: 'Heart' },
  { id: 'curator', name: 'Curator', description: 'Bookmark 5 posts', icon: 'Bookmark' },
  { id: 'explorer', name: 'Explorer', description: 'Visit all categories', icon: 'Compass' },
  { id: 'affirmed', name: 'Affirmed', description: 'Read all affirmations', icon: 'Sparkles' },
  { id: 'early-bird', name: 'Early Bird', description: 'Read before 8 AM', icon: 'Sun' },
  { id: 'night-owl', name: 'Night Owl', description: 'Read after 10 PM', icon: 'MoonStar' },
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatTimeAgo = (timestamp) => {
  const date = new Date(timestamp);
  const seconds = Math.floor((Date.now() - date) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 2592000) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString('en', { month: 'short', day: 'numeric' });
};

const FriendProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: me, isLoading: authLoading } = useAuth();
  const { getPostById } = useContent();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('badges');

  useEffect(() => {
    if (authLoading) return;
    if (!me) {
      navigate('/auth');
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('rbr_token');
        const res = await fetch(`${API_URL}/api/users/${userId}/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 403) {
          setError('You must be friends to view this profile.');
          return;
        }
        if (!res.ok) {
          setError('Could not load profile.');
          return;
        }
        setProfile(await res.json());
      } catch (e) {
        setError('Network error');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId, me, authLoading, navigate]);

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-stone-500 text-sm">Loading profile...</p>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-5 text-center">
        <p className="text-stone-500 mb-4">{error || 'Profile not found.'}</p>
        <button
          onClick={() => navigate('/profile')}
          className="px-5 py-2.5 btn-gold rounded-full text-sm font-medium"
          data-testid="friend-profile-back-btn"
        >
          Back to my profile
        </button>
      </div>
    );
  }

  const earnedSet = new Set(profile.badges || []);
  const earnedBadges = BADGE_CATALOG.filter((b) => earnedSet.has(b.id));
  const lockedBadges = BADGE_CATALOG.filter((b) => !earnedSet.has(b.id));
  const streak = profile.streak || { current: 0, longest: 0, history: [] };
  const comments = profile.comments || [];
  const readIds = profile.readPosts || [];

  // 30-day streak calendar
  const calendarDays = (() => {
    const days = [];
    const today = new Date();
    const history = new Set(streak.history || []);
    for (let i = 29; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const str = d.toISOString().split('T')[0];
      days.push({ date: str, active: history.has(str), isToday: i === 0, dayNum: d.getDate() });
    }
    return days;
  })();

  return (
    <div className="animate-fade-in pb-6" data-testid="friend-profile-page">
      {/* Header */}
      <div className="px-5 pt-4 flex items-center justify-between mb-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-stone-500 hover:text-[#D6AD60] transition-colors duration-300"
          data-testid="friend-profile-back"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back</span>
        </button>
        <span className="text-[10px] font-medium text-emerald-400 uppercase tracking-wider">Friend</span>
      </div>

      {/* Profile card */}
      <div className="px-5 mb-6">
        <div className="bg-[#FFFCF3] border border-stone-200 rounded-3xl p-5 shadow-md">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-2xl overflow-hidden bg-gradient-to-br from-[#D6AD60] to-[#3a322a] flex items-center justify-center shadow-md flex-shrink-0">
              {profile.profilePicture || profile.avatar ? (
                <img
                  src={profile.profilePicture || profile.avatar}
                  alt={profile.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-10 h-10 text-white" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2
                className="text-lg font-semibold text-stone-800 truncate"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="friend-profile-name"
              >
                {profile.name}
              </h2>
              {profile.joinDate && (
                <p className="text-[10px] text-stone-500 mt-1">
                  Joined {new Date(profile.joinDate).toLocaleDateString('en', { month: 'long', year: 'numeric' })}
                </p>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mt-5">
            <div className="bg-[#E8DDCB] rounded-2xl p-3 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Flame className="w-4 h-4 text-orange-400" />
                <span className="text-lg font-bold text-stone-800" data-testid="friend-streak-current">
                  {streak.current || 0}
                </span>
              </div>
              <p className="text-[10px] text-stone-500">Day Streak</p>
            </div>
            <div className="bg-[#E8DDCB] rounded-2xl p-3 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Award className="w-4 h-4 text-amber-400" />
                <span className="text-lg font-bold text-stone-800" data-testid="friend-badges-count">
                  {earnedBadges.length}
                </span>
              </div>
              <p className="text-[10px] text-stone-400">Badges</p>
            </div>
            <div className="bg-[#E8DDCB] rounded-2xl p-3 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <BookOpen className="w-4 h-4 text-emerald-400" />
                <span className="text-lg font-bold text-stone-800" data-testid="friend-read-count">
                  {readIds.length}
                </span>
              </div>
              <p className="text-[10px] text-stone-500">Read</p>
            </div>
          </div>
        </div>
      </div>

      {/* Streak Calendar */}
      <div className="px-5 mb-6">
        <div className="bg-[#FFFCF3] border border-stone-200 rounded-3xl p-5 shadow-md">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-400" />
              <h3 className="text-sm font-semibold text-stone-800" style={{ fontFamily: "'Playfair Display', serif" }}>
                Activity Streak
              </h3>
            </div>
            <span className="text-[10px] text-stone-500">Best: {streak.longest || 0} days</span>
          </div>
          <div className="grid grid-cols-10 gap-1.5">
            {calendarDays.map((day) => (
              <div
                key={day.date}
                title={day.date}
                className={`aspect-square rounded-lg flex items-center justify-center text-[9px] font-medium ${
                  day.active
                    ? 'bg-orange-400 text-white shadow-sm'
                    : day.isToday
                    ? 'bg-[#E8DDCB] text-[#D6AD60] ring-1 ring-[#D6AD60]/40'
                    : 'bg-[#E8DDCB] text-stone-300'
                }`}
              >
                {day.dayNum}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-5 mb-4">
        <div className="flex bg-[#FFFCF3] border border-stone-200 rounded-2xl p-1 shadow-md">
          {[
            { id: 'badges', label: 'Badges', icon: Trophy },
            { id: 'read', label: 'Read', icon: BookOpen },
            { id: 'comments', label: 'Comments', icon: MessageCircle },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-medium transition-all duration-300 ${
                activeTab === tab.id ? 'bg-gradient-to-br from-[#D6AD60] to-[#D6AD60] text-[#1a1814] shadow-md' : 'text-stone-500 hover:text-[#D6AD60]'
              }`}
              data-testid={`friend-tab-${tab.id}`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="px-5">
        {activeTab === 'badges' && (
          <div className="animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs text-[#D6AD60]">
                {earnedBadges.length} of {BADGE_CATALOG.length} earned
              </span>
              <div className="flex-1 mx-3 h-1.5 bg-[#E8DDCB] rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#D6AD60] to-[#D6AD60] rounded-full transition-all duration-500"
                  style={{ width: `${(earnedBadges.length / BADGE_CATALOG.length) * 100}%` }}
                />
              </div>
            </div>
            {earnedBadges.length > 0 && (
              <div className="mb-5">
                <h4 className="text-xs font-semibold text-[#D6AD60] uppercase tracking-wider mb-3">Earned</h4>
                <div className="grid grid-cols-3 gap-3">
                  {earnedBadges.map((badge) => {
                    const Icon = iconMap[badge.icon] || Trophy;
                    return (
                      <div key={badge.id} className="bg-[#FFFCF3] border border-[#D6AD60]/25 rounded-2xl p-3 shadow-md text-center">
                        <div className="w-11 h-11 rounded-xl bg-[#E8DDCB] flex items-center justify-center mx-auto mb-2">
                          <Icon className="w-5 h-5 text-[#AA8347]" />
                        </div>
                        <p className="text-[11px] font-semibold text-stone-800 leading-tight">{badge.name}</p>
                        <p className="text-[9px] text-stone-500 mt-0.5 leading-tight">{badge.description}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {lockedBadges.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Locked</h4>
                <div className="grid grid-cols-3 gap-3">
                  {lockedBadges.map((badge) => {
                    const Icon = iconMap[badge.icon] || Trophy;
                    return (
                      <div key={badge.id} className="bg-[#FFFCF3]/60 border border-stone-100 rounded-2xl p-3 text-center opacity-60">
                        <div className="w-11 h-11 rounded-xl bg-[#E8DDCB] flex items-center justify-center mx-auto mb-2">
                          <Icon className="w-5 h-5 text-stone-300" />
                        </div>
                        <p className="text-[11px] font-semibold text-stone-500 leading-tight">{badge.name}</p>
                        <p className="text-[9px] text-stone-400 mt-0.5 leading-tight">{badge.description}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'read' && (
          <div className="animate-fade-in">
            {readIds.length === 0 ? (
              <div className="text-center py-12">
                <BookOpen className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                <p className="text-sm text-stone-500">No reflections read yet</p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {readIds.map((pid) => {
                  const post = getPostById(pid);
                  if (!post) return null;
                  return (
                    <Link
                      key={pid}
                      to={`/post/${pid}`}
                      className="flex items-center gap-3 bg-[#FFFCF3] border border-stone-200 rounded-2xl p-3 shadow-md hover:border-[#D6AD60]/30 hover:shadow-lg transition-all duration-300"
                      data-testid={`friend-read-${pid}`}
                    >
                      <img src={post.image} alt={post.title} className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-stone-800 truncate" style={{ fontFamily: "'Playfair Display', serif" }}>
                          {post.title}
                        </p>
                        <p className="text-[10px] text-stone-500 mt-0.5 capitalize">{post.category.replace('-', ' ')}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'comments' && (
          <div className="animate-fade-in">
            {comments.length === 0 ? (
              <div className="text-center py-12">
                <MessageCircle className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                <p className="text-sm text-stone-500">No comments yet</p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {comments.map((c) => {
                  const post = getPostById(c.postId);
                  const style = getCategoryStyle(post?.category);
                  return (
                    <Link
                      key={c.id}
                      to={`/post/${c.postId}`}
                      className={`bg-[#FFFCF3] border border-stone-200 rounded-2xl p-4 shadow-md hover:border-[#D6AD60]/30 hover:shadow-lg transition-all duration-300 block border-l-4 ${style.border}`}
                      data-testid={`friend-comment-${c.id}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className={`w-1.5 h-1.5 rounded-full ${style.dot} flex-shrink-0`} />
                          <span className={`text-[10px] font-semibold uppercase tracking-wider ${style.text} flex-shrink-0`}>
                            {style.label}
                          </span>
                          <span className="text-[11px] text-stone-500 truncate">· {post?.title || 'a reflection'}</span>
                        </div>
                        <span className="text-[10px] text-stone-500 flex-shrink-0">{formatTimeAgo(c.createdAt)}</span>
                      </div>
                      <p className="text-sm text-stone-700 leading-snug break-words">{c.content}</p>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FriendProfilePage;
